import { AlertCircle, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  type UseOrganizationsCatalogResult,
  useOrganizationsCatalog,
} from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import type { UsePayoutBatchesListResult } from "../../hooks/usePayoutBatchesList";
import { batchPath, newBatchPath } from "../../utils/batchLinks";
import { canWriteBatches } from "../../utils/batchPermissions";
import { BatchesFilters } from "./BatchesFilters";
import { BatchesKpiStrip } from "./BatchesKpiStrip";
import { BatchesTable } from "./BatchesTable";

export interface BatchesSectionProps {
  list: UsePayoutBatchesListResult;
  /** INTERNAL only; null for members, who see no organization column or filter. */
  catalog: UseOrganizationsCatalogResult | null;
}

/** Filters, KPI strip and table of the list; the organizations catalog is optional so members never load it. */
export function BatchesSection({ list, catalog }: BatchesSectionProps) {
  const { t } = useTranslations("payoutBatches");
  const navigate = useNavigate();
  const { user } = useAuth();

  const organizationNames = useMemo(() => {
    if (!catalog) return null;
    return new Map(catalog.organizations.map((organization) => [organization.id, organization.name]));
  }, [catalog]);

  return (
    <>
      <BatchesFilters
        catalog={catalog}
        organizationId={list.organizationId}
        onOrganizationChange={list.setOrganizationId}
        corridor={list.corridor}
        onCorridorChange={list.setCorridor}
      />
      <BatchesKpiStrip stats={list.stats} active={list.kpi} onChange={list.setKpi} />
      {list.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("error_load")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={() => void list.refresh()}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      ) : (
        <BatchesTable
          rows={list.rows}
          isLoading={list.isLoading}
          page={list.page}
          total={list.total}
          onPageChange={list.setPage}
          onRowClick={(batch) => navigate(batchPath(batch.id))}
          organizationNames={organizationNames}
          hasActiveFilters={list.hasActiveFilters}
          onClearFilters={list.clearFilters}
          onNewBatch={canWriteBatches(user) ? () => navigate(newBatchPath()) : undefined}
        />
      )}
    </>
  );
}

/** Staff variant: mounts the organizations catalog (`organizations:read`) for the filter and the names column. */
export function InternalBatchesSection({ list }: { list: UsePayoutBatchesListResult }) {
  const catalog = useOrganizationsCatalog();
  return <BatchesSection list={list} catalog={catalog} />;
}
